import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AccountSettingPayload,
  AccountSettingsService,
  FindAllAccountSettingsPayload,
} from "../services/account-settings.service";

const ACCOUNT_SETTINGS_LIST_KEY = "pret-account-settings-list";
const ACCOUNT_SETTING_BY_ID_KEY = "pret-account-setting-by-id";

export const useAccountSettings = (filters: FindAllAccountSettingsPayload) => {
  const {
    data: accountSettings,
    isLoading: isLoadingAccountSettings,
    isFetching: isFetchingAccountSettings,
    refetch: refetchAccountSettings,
  } = useQuery({
    queryKey: [ACCOUNT_SETTINGS_LIST_KEY, filters],
    queryFn: () => AccountSettingsService.findAll(filters),
  });

  return {
    accountSettings,
    isLoadingAccountSettings,
    isFetchingAccountSettings,
    refetchAccountSettings,
  };
};

export const useAccountSetting = (id: string, enabled = true) => {
  const {
    data: accountSetting,
    isLoading: isLoadingAccountSetting,
    refetch: refetchAccountSetting,
  } = useQuery({
    queryKey: [ACCOUNT_SETTING_BY_ID_KEY, id],
    queryFn: () => AccountSettingsService.findOne(id),
    enabled: !!id && enabled,
  });

  return {
    accountSetting,
    isLoadingAccountSetting,
    refetchAccountSetting,
  };
};

export const useCreateAccountSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AccountSettingPayload) =>
      AccountSettingsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_SETTINGS_LIST_KEY] });
    },
  });
};

export const useUpdateAccountSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AccountSettingPayload }) =>
      AccountSettingsService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_SETTINGS_LIST_KEY] });
      queryClient.invalidateQueries({
        queryKey: [ACCOUNT_SETTING_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveAccountSetting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => AccountSettingsService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACCOUNT_SETTINGS_LIST_KEY] });
    },
  });
};
